import { Form, Tooltip } from 'antd';
import { Loadable } from 'components';
import { AgentMapRefProps } from 'components/access-mange-dtv/AgentMap';
import AgentMapName from 'components/agent-map/AgentMapName';
import { useAgentMapDetailDashboard, useTitle } from 'hooks';
import { useAgentMapStore } from 'hooks/agent-maps/useAgentMapStore';
import { useProfile } from 'hooks';
import { sidebarMenuMessages, validateMessages } from 'messages';
import { agentMapsMessages } from 'messages/agent-maps.messages';
import React, { lazy, memo, useEffect, useRef } from 'react';
import { CreateAgentMapSeatDashboardDto } from 'types';
import { FabricAgentMapOtherObject, FabricAgentMapSeatDashboard } from 'types/fabric-agent-map';
import { SingleAgentMapSingleStatusesType } from './AccessManageDtv';
import { socketService } from 'utils/agent-map/socket-service-singleton'; // Import the socket service
import { useDispatch } from 'react-redux';
import { resetData } from 'store/features/agent-map'; // Import the reset action
import { checkPermissions } from 'utils/check-permission';
import { PERMISSION } from 'utils';

const AgentMap = lazy(() => import('components/access-mange-dtv/AgentMap'));

export type AgentMapFormTypeDashboard = CreateAgentMapSeatDashboardDto & {
  fabricSeatsDashboard: FabricAgentMapSeatDashboard[];
  fabricOtherObjects: FabricAgentMapOtherObject[];
};

interface AgentMapFormProps {
  agentMapId: number;
  singleAgentMapAgentStatuses: SingleAgentMapSingleStatusesType[];
  showCounters: boolean;
}

/**
 * Component hiển thị một bản đồ agent cụ thể
 */
const AccessManageDtvAgentMap: React.FC<AgentMapFormProps> = ({
  agentMapId,
  singleAgentMapAgentStatuses,
  showCounters
}) => {
  useTitle(sidebarMenuMessages.agentMaps);
  const agentRef = useRef<AgentMapRefProps>(null);
  const [form] = Form.useForm<AgentMapFormTypeDashboard>();
  const dispatch = useDispatch(); // Add Redux dispatch
  const { userPermissionIds } = useProfile();

  // Use data from Redux store - including the actual timers and counters objects
  const {
    mapData,
    timers,        // Lấy đối tượng timers thay vì hàm getSeatTimer
    counters,      // Lấy đối tượng counters thay vì hàm getSeatCounters
    updateSeatTimer,
    selectMaps, // Destructure selectMaps
  } = useAgentMapStore();

  // Initial data loading
  useAgentMapDetailDashboard(agentMapId, form);

  // Effect for component mount (socket reinitialization) and unmount (cleanup)
  useEffect(() => {
    const isAllowViewAgentMap = checkPermissions(userPermissionIds, PERMISSION.READ_AGENT_MAP);

    if (isAllowViewAgentMap) {

      socketService.reinitialize(); // Force reinitialization on mount
    } else {

    }

    return () => {
      // Clear agent map state on unmount

      dispatch(resetData());
    };
  }, [dispatch, userPermissionIds]); // Add userPermissionIds to dependencies

  // Effect for handling window focus
  // useEffect(() => {
  //   const handleFocus = () => {
  //     
  //     socketService.reinitialize(); // Reinitialize socket on window focus
  //     if (typeof selectMaps === 'function' && agentMapId) {
  //       // Re-fetch data for the current map by calling selectMaps with forceRefresh
  //       selectMaps([agentMapId], true);
  //     }
  //   };

  //   window.addEventListener('focus', handleFocus);
  //   return () => {
  //     window.removeEventListener('focus', handleFocus);
  //   };
  // }, [agentMapId, selectMaps]); // Re-register if agentMapId or selectMaps changes

  const fabricSeatsDashboard = Form.useWatch('fabricSeatsDashboard', form) || [];
  const fabricOtherObjects = Form.useWatch('fabricOtherObjects', form) || [];

  return (
    <div className='h-full w-full overflow-hidden'>
      <Form
        labelAlign='right'
        scrollToFirstError={{ behavior: 'smooth', block: 'start' }}
        form={form}
        labelCol={{
          flex: '180px'
        }}
        layout='horizontal'
        validateMessages={validateMessages}
        className='flex h-full w-full'
        initialValues={{
          name: agentMapsMessages.newMap
        }}
      >
        <div className='flex h-full w-full flex-col overflow-hidden'>
          <div className='h-full w-full max-w-full'>
            <Form.Item<AgentMapFormTypeDashboard> name='fabricSeatsDashboard' noStyle />
            <Form.Item<AgentMapFormTypeDashboard> name='fabricOtherObjects' noStyle />

            <Loadable message={agentMapsMessages.drawGrid}>
              <AgentMap
                singleAgentMapAgentStatuses={singleAgentMapAgentStatuses}
                ref={agentRef}
                seats={fabricSeatsDashboard}
                otherObjects={fabricOtherObjects}
                seatTimers={timers}           // Truyền đối tượng timers không phải hàm
                updateSeatTimer={updateSeatTimer}
                showCounters={showCounters}
                seatCounters={counters}       // Truyền đối tượng counters không phải hàm
              />
            </Loadable>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default memo(AccessManageDtvAgentMap);
